'use client'

import { formatDuration, formatPrice } from '@/lib/c2c/format'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { CrawlSafeLink } from '@/components/crawl-safe-link'

import { DurationConnector } from './duration-connector'
import { BusIcon } from './icons/bus-icon'
import { TrainIcon } from './icons/train-icon'
import { StarRating } from './star-rating'

interface TripCardProps {
    departTime: string
    arriveTime: string
    departStation: string
    arriveStation: string
    duration: number
    price?: number
    carrierName: string
    carrierLogo?: string
    carrierRating?: number
    isLuxury?: boolean
    isNextGen?: boolean
    searchUrl: string
    transfers?: number
    mode: string
    /** Fired before navigation when the card is clicked. */
    onSelect?: () => void
}

const tooltipContentClass = 'rounded-none bg-[#2e3033] px-5 py-4 text-sm text-white'

export function TripCard({
    departTime,
    arriveTime,
    departStation,
    arriveStation,
    duration,
    price,
    carrierName,
    carrierLogo,
    carrierRating,
    isLuxury = false,
    isNextGen = false,
    searchUrl,
    transfers = 0,
    mode,
    onSelect,
}: TripCardProps) {
    const formattedDuration = formatDuration(duration)
    const modeLabel = mode.charAt(0).toUpperCase() + mode.slice(1)
    // The glyph tracks the route's transport mode — a bus icon on a train trip is what
    // legacy never showed and readers notice immediately. Bus stays the fallback for the
    // modes with no dedicated icon (ferry/flight), matching the pre-existing behaviour.
    const ModeIcon = mode === 'train' ? TrainIcon : BusIcon
    const modeIconTestId = `trip-mode-icon-${mode === 'train' ? 'train' : 'bus'}`
    const badges = [isLuxury ? 'Luxury' : null, isNextGen ? 'NextGen' : null].filter(
        (b) => b !== null,
    )

    return (
        <CrawlSafeLink
            url={searchUrl}
            onSelect={onSelect}
            testId="trip-card"
            className="block w-full cursor-pointer rounded-[7px] border border-[#e2e2e2] bg-white p-4 text-left transition-shadow hover:shadow-md md:px-6 md:py-5"
        >
            {/* Row 1: Carrier info + rating + price */}
            <div className="mb-3 flex items-center gap-2 md:mb-4">
                {/* Mobile: mode icon + carrier name text */}
                <span className="md:hidden">
                    <ModeIcon
                        className="h-4 w-4 shrink-0 text-[#878787]"
                        data-testid={modeIconTestId}
                    />
                </span>
                {carrierLogo ? (
                    <>
                        <span className="truncate text-sm text-[#767676] uppercase md:hidden">
                            {carrierName}
                        </span>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    src={carrierLogo}
                                    alt={carrierName}
                                    className="hidden h-5 max-w-[80px] object-contain md:block"
                                />
                            </TooltipTrigger>
                            <TooltipContent side="top" className={tooltipContentClass}>
                                {carrierName} ({modeLabel})
                            </TooltipContent>
                        </Tooltip>
                    </>
                ) : (
                    <span className="truncate text-sm text-[#767676] uppercase">{carrierName}</span>
                )}
                {carrierRating != null && <StarRating rating={carrierRating} className="md:ml-1" />}
                {price != null && (
                    <span className="ml-auto shrink-0 text-lg font-bold text-[#007380] md:text-xl md:text-[#44474a]">
                        {formatPrice(price)}
                    </span>
                )}
            </div>

            {/* Row 2: Times + duration */}
            <div className="flex items-start">
                {/* Mode circle icon — desktop only */}
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div className="mr-4 hidden h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-white bg-[#ebeef3] md:flex">
                            <ModeIcon
                                className="w-[26px] text-[#44474a]"
                                data-testid={modeIconTestId}
                            />
                        </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className={tooltipContentClass}>
                        {modeLabel}
                    </TooltipContent>
                </Tooltip>

                <div className="flex min-w-0 flex-1 items-baseline gap-1 md:items-start md:gap-0">
                    {/* Departure */}
                    <div className="shrink-0 md:min-w-0 md:flex-[2]">
                        <p className="text-lg font-bold text-[#2e3033] md:text-xl">{departTime}</p>
                        <p className="hidden truncate text-sm leading-[21px] text-[#2e3033] md:block">
                            {departStation}
                        </p>
                    </div>

                    {/* Duration — mobile: inline text, desktop: connector line */}
                    <span className="text-xs text-[#767676] italic md:hidden">
                        {formattedDuration}
                    </span>
                    <div className="hidden md:flex md:flex-1 md:items-center md:px-3">
                        <DurationConnector duration={formattedDuration} />
                    </div>

                    {/* Arrival */}
                    <div className="shrink-0 md:min-w-0 md:flex-[2]">
                        <p className="text-lg font-bold text-[#2e3033] md:text-xl">{arriveTime}</p>
                        <p className="hidden truncate text-sm leading-[21px] text-[#2e3033] md:block">
                            {arriveStation}
                        </p>
                    </div>
                </div>
            </div>

            {/* Row 3: Stations — mobile only (desktop shows them under each time above) */}
            <div className="flex items-center gap-4 text-sm text-[#2e3033] md:hidden">
                <span className="truncate">{departStation}</span>
                <span className="truncate">{arriveStation}</span>
            </div>

            {/* Transfers indicator */}
            {transfers > 0 && (
                <p className="mt-2 text-xs text-[#767676] md:pl-[60px]">
                    {transfers} transfer{transfers > 1 ? 's' : ''}
                </p>
            )}

            {/* Feature chips. Legacy renders exactly these two on the schedule (timeTable)
                variant, sharing one style, and suppresses the transfers pill there —
                ui-react SearchResult.tsx `details-container`. */}
            {badges.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                    {badges.map((badge) => (
                        <span
                            key={badge}
                            className="inline-block rounded-2xl bg-[#e2d3fa] px-3 text-sm text-[#6341b4]"
                        >
                            {badge}
                        </span>
                    ))}
                </div>
            )}
        </CrawlSafeLink>
    )
}
